// FILE: src/features/ticketSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

// Fetch all support tickets
export const fetchTickets = createAsyncThunk(
  "tickets/fetchTickets",
  async ({ category, status }) => {
    // Build query params
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (status) params.set("status", status);

    // Just provide the relative path to fetchWithAuth
    const url = `/api/support?${params.toString()}`;
    // fetchWithAuth returns the parsed JSON
    return await fetchWithAuth(url);
  }
);

// Fetch a single ticket by ID
export const fetchTicketDetail = createAsyncThunk(
  "tickets/fetchTicketDetail",
  async (ticketId) => {
    const url = `/api/support/${ticketId}`;
    return await fetchWithAuth(url);
  }
);

// Update or resolve a ticket
export const updateTicket = createAsyncThunk(
  "tickets/updateTicket",
  async ({ ticketId, updates }) => {
    const url = `/api/support/${ticketId}`;
    return await fetchWithAuth(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates)
    });
  }
);

// The list (loading / error) and the open ticket (detailLoading /
// detailError) load separately, so one never hides the other. A refused
// update is reported by the page that asked (B-11), not stored here.
const ticketSlice = createSlice({
  name: "tickets",
  initialState: {
    list: [],
    loading: false,
    error: null,
    currentTicket: null,
    detailLoading: false,
    detailError: null
  },
  reducers: {
    clearCurrentTicket(state) {
      state.currentTicket = null;
      state.detailError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchTickets
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.list = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })

      // fetchTicketDetail
      .addCase(fetchTicketDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchTicketDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentTicket = action.payload;
      })
      .addCase(fetchTicketDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.error;
      })

      // updateTicket: PUT /api/support/:id answers { message, ticket } with
      // the student not expanded, so keep the expanded student we have.
      .addCase(updateTicket.fulfilled, (state, action) => {
        const updated = action.payload?.ticket || action.payload;
        if (!updated?._id) return;
        const index = state.list.findIndex((t) => t._id === updated._id);
        if (index !== -1) {
          state.list[index] = { ...updated, userId: state.list[index].userId };
        }
        if (state.currentTicket && state.currentTicket._id === updated._id) {
          state.currentTicket = { ...updated, userId: state.currentTicket.userId };
        }
      });
  }
});

export const { clearCurrentTicket } = ticketSlice.actions;
export default ticketSlice.reducer;
